import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Gift, Coins, Users, Store, Share2, ClipboardCopy, Sparkles,
  Instagram, Facebook, Twitter, MessageCircle, Phone, ArrowRight, ShoppingBag,
  Lock, Info, Zap, Star,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import { LanguageProvider } from '../context/LanguageContext';
import ReferralFloatingCTA from '../components/ReferralFloatingCTA';
import RewardCelebration, { celebrate } from '../components/RewardCelebration';
import RewardMilestonesRow from '../components/RewardMilestonesRow';
import RedeemGiftModal from '../components/RedeemGiftModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHARE_PROVIDERS = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram, url: 'https://www.instagram.com' },
  { key: 'tiktok',    label: 'TikTok',    Icon: Sparkles,  url: 'https://www.tiktok.com' },
  { key: 'facebook',  label: 'Facebook',  Icon: Facebook,  url: 'https://www.facebook.com/sharer/sharer.php?u={URL}' },
  { key: 'twitter',   label: 'X / Twitter', Icon: Twitter, url: 'https://twitter.com/intent/tweet?text=Try%20NoFee&url={URL}' },
  { key: 'whatsapp',  label: 'WhatsApp',  Icon: MessageCircle, url: 'https://wa.me/?text=Try%20NoFee%20{URL}' },
  { key: 'sms',       label: 'SMS',       Icon: Phone, url: 'sms:?&body=Try%20NoFee%20{URL}' },
];

const StatusPill = ({ status }) => {
  const map = {
    approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${map[status] || map.pending}`}>
      {status}
    </span>
  );
};

const Inner = () => {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [redeemOpen, setRedeemOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/customer?login=1', { replace: true });
      return;
    }
    axios
      .get(`${API}/customers/me/rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem('nofee_customer_token');
          navigate('/customer?login=1', { replace: true });
        } else {
          toast.error('Failed to load your rewards.');
        }
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const refresh = async () => {
    const r = await axios.get(`${API}/customers/me/rewards`, { headers: { Authorization: `Bearer ${token}` } });
    setData(r.data);
  };

  const fullReferralUrl = useMemo(() => {
    if (!data?.summary?.referral_code) return '';
    return `${window.location.origin}/customer?ref=${data.summary.referral_code}`;
  }, [data]);

  const copyLink = async () => {
    if (!fullReferralUrl) return;
    try {
      await navigator.clipboard.writeText(fullReferralUrl);
      toast.success('Referral link copied!');
    } catch { toast.error('Could not copy.'); }
  };

  const recordShare = async (network) => {
    try {
      const r = await axios.post(
        `${API}/customers/share`,
        { network, url: fullReferralUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.awarded) {
        celebrate({
          kind: 'points',
          title: `+${r.data.points} Points Earned`,
          subtitle: `Referral shared on ${network}`,
        });
        refresh();
      } else {
        toast(`Already shared on ${network} today.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record share.');
    }
  };

  // Tied to a REAL share action — never on bare click. Points are
  // only recorded if window.open returned a valid window (popup not
  // blocked) for the platforms that ship a web share intent; for
  // Instagram/TikTok (no share URL exists) we copy the link and skip
  // auto-recording — the user has to use the /account/referrals page
  // to confirm an Instagram/TikTok post and claim the reward there.
  const openShare = (provider) => {
    if (!fullReferralUrl) return;
    const hasShareIntent = provider.url.includes('{URL}');
    if (!hasShareIntent) {
      // Instagram / TikTok — no programmatic share. Copy link only;
      // do NOT credit points (the spec requires a real share action).
      navigator.clipboard?.writeText?.(fullReferralUrl).catch(() => {});
      toast(`Link copied. Paste it into ${provider.label} to share — points credited from your Referrals page.`);
      return;
    }
    const u = provider.url.replace('{URL}', encodeURIComponent(fullReferralUrl));
    const win = window.open(u, '_blank', 'noopener,noreferrer');
    if (!win) {
      toast.error('Your browser blocked the share popup. Please allow popups and try again.');
      return;
    }
    recordShare(provider.key);
  };

  const redeem = async (points) => {
    try {
      const r = await axios.post(
        `${API}/customers/redeem`,
        { points },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.ok) {
        const dollars = (r.data.gift_credit_cents_added / 100).toFixed(2);
        celebrate({
          kind: 'credit',
          title: `Gift Credits Unlocked`,
          subtitle: `$${dollars} added to your balance`,
        });
        refresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not redeem.');
    }
  };

  if (loading || !data) {
    return (
      <div className="pt-32 text-center min-h-screen">
        <div className="inline-block w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { summary, config, history, restaurant_referrals: restRefs, friends_invited: friends, tier } = data;
  const ptsPerDollar = config.points_per_dollar_gift_credit || 20;
  const ptsPerDollarSpent = config.points_per_dollar_spent || 1;
  const ptsBalance = summary.total_points;
  const giftCreditBalance = (summary.gift_credit_cents / 100).toFixed(2);
  const nextDollar = ptsBalance % ptsPerDollar === 0 ? ptsBalance + ptsPerDollar : Math.ceil(ptsBalance / ptsPerDollar) * ptsPerDollar;
  const ptsToNext = nextDollar - ptsBalance;
  const progress = ptsPerDollar > 0 ? ((ptsPerDollar - ptsToNext) / ptsPerDollar) * 100 : 0;
  const pointsFromOrders = summary.points_from_orders || 0;
  const ordersWithPoints = summary.orders_with_points || 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="my-rewards-page">
      <Navbar />

      {/* HERO summary */}
      <section className="pt-32 lg:pt-36 pb-10">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
            My Rewards
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-tight">
            Earn points. <span className="text-gold-400">Unlock rewards at your favorite restaurants.</span>
          </h1>

          {/* Loyalty earn banner — premium, single line, never spammy */}
          <div
            className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-gold-500/25 bg-gradient-to-r from-zinc-950 via-black to-zinc-950 px-4 sm:px-5 py-3.5"
            data-testid="earn-on-orders-banner"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-4 h-4 text-black" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-gold-300 font-bold">
                Earn on every order
              </div>
              <div className="text-[14px] sm:text-[15px] text-white font-semibold">
                <span className="text-gold-400 font-extrabold">${ptsPerDollarSpent} = {ptsPerDollarSpent} point</span>
                <span className="text-white/65 font-medium ml-1.5">— points convert to gift credits, discounts &amp; VIP perks.</span>
              </div>
            </div>
          </div>

          {/* HOW REWARDS WORK — premium explanatory block (single source of truth) */}
          <div
            data-testid="how-rewards-work"
            className="mt-6 rounded-2xl border border-gold-500/25 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 overflow-hidden"
          >
            <div className="px-5 sm:px-7 py-5 border-b border-white/[0.06] flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gold-500/[0.10] border border-gold-500/40 flex items-center justify-center">
                <Info className="w-4 h-4 text-gold-300" strokeWidth={2.1} />
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-300 font-bold">
                  How Rewards Work
                </p>
                <p className="text-[12.5px] text-white/55 leading-snug mt-0.5">
                  Simple. Premium. Restaurant-focused.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
              {/* EARN */}
              <div className="p-5 sm:p-7">
                <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-3">
                  Step 1 — Earn points
                </p>
                <ul className="space-y-2.5">
                  {[
                    { icon: ShoppingBag, label: 'Order food', value: `+${ptsPerDollarSpent} pt per $1` },
                    { icon: Zap, label: 'Verify on-time delivery', value: '+2 pts per order' },
                    { icon: Star, label: 'Verify + leave a review', value: '+3 pts per order' },
                    { icon: Users, label: 'Invite a friend', value: `+${config.points_per_referral_signup} pts on signup` },
                    { icon: Gift, label: "Friend's first order", value: `+${config.points_per_referral_first_order} pts` },
                    { icon: Store, label: 'Refer a restaurant', value: 'Bonus after activation' },
                  ].map(({ icon: I, label, value }) => (
                    <li key={label} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/10 flex items-center justify-center flex-shrink-0">
                        <I className="w-4 h-4 text-gold-400" strokeWidth={1.7} />
                      </div>
                      <div className="flex-1 flex items-center justify-between gap-3 min-w-0">
                        <span className="text-[13.5px] text-white/85 font-medium truncate">{label}</span>
                        <span className="text-[12px] font-bold text-gold-300 whitespace-nowrap">{value}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* REDEEM */}
              <div className="p-5 sm:p-7 bg-gradient-to-br from-gold-500/[0.04] to-transparent">
                <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-3">
                  Step 2 — Use your rewards
                </p>
                <div className="rounded-xl border border-gold-500/40 bg-gold-500/[0.06] px-4 py-3.5 flex items-center gap-3">
                  <div className="text-2xl font-black text-gold-300 leading-none">{ptsPerDollar}</div>
                  <ArrowRight className="w-4 h-4 text-white/40" />
                  <div className="text-2xl font-black text-white leading-none">$1</div>
                  <span className="text-[11.5px] text-white/55 ml-1">NoFee Gift Credit</span>
                </div>
                <p className="mt-3 text-[13px] text-white/65 leading-relaxed">
                  Convert points to NoFee credit and use it at any participating restaurant on NoFee.
                </p>
                <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08]">
                  <Lock className="w-3.5 h-3.5 text-white/45 mt-0.5 flex-shrink-0" />
                  <p className="text-[11.5px] text-white/55 leading-snug">
                    Gift credits are NoFee platform credits — <span className="text-white/85 font-semibold">not redeemable for cash</span> and not transferable outside the NoFee network.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Minimal 3-tier reward milestones (horizontal premium row) */}
          <RewardMilestonesRow milestones={config.reward_milestones || []} points={ptsBalance} />

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard
              icon={Coins}
              label="Total Points"
              value={ptsBalance.toLocaleString()}
              foot={`${ptsToNext} pts to next $1`}
              testid="stat-total-points"
            />
            <StatCard
              icon={ShoppingBag}
              label="From Orders"
              value={pointsFromOrders.toLocaleString()}
              foot={`${ordersWithPoints} order${ordersWithPoints === 1 ? '' : 's'} · ${ptsPerDollarSpent} pt / $1`}
              testid="stat-points-from-orders"
            />
            <StatCard
              icon={Gift}
              label="Gift Credit Balance"
              value={`$${giftCreditBalance}`}
              foot={`${ptsPerDollar} pts = $1.00`}
              testid="stat-gift-credit"
            />
            <StatCard
              icon={Users}
              label="Friends Invited"
              value={friends.length}
              foot={`${friends.filter((f) => f.first_order_recorded).length} ordered`}
              testid="stat-friends"
            />
            <StatCard
              icon={Store}
              label="Restaurants Referred"
              value={restRefs.length}
              foot={`${restRefs.filter((r) => r.subscription_status === 'active').length} active`}
              testid="stat-rest-refs"
            />
          </div>

          {/* Progress bar to next dollar */}
          <div className="mt-6 rounded-2xl border border-gold-500/20 bg-gradient-to-r from-zinc-950 to-black p-4">
            <div className="flex items-center justify-between text-[12px] text-white/65 mb-2">
              <span>Progress to your next $1 gift credit</span>
              <span className="text-gold-300 font-semibold">{ptsBalance % ptsPerDollar} / {ptsPerDollar} pts</span>
            </div>
            <div className="h-2 bg-white/[0.05] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-300 to-gold-500" style={{ width: `${Math.min(100, progress)}%` }} />
            </div>
          </div>
        </div>
      </section>

      {/* Referral link + social share */}
      <section className="pb-14">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-5 lg:gap-7">
          {/* LINK */}
          <div className="rounded-2xl border border-gold-500/25 bg-gradient-to-br from-zinc-950 to-black p-6 lg:p-7">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-400 font-semibold mb-2">Your referral link</p>
            <h2 className="text-xl font-extrabold text-white tracking-tight">Invite friends. Earn points.</h2>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">
              Each friend that signs up: <span className="text-white">+{config.points_per_referral_signup} pts</span>.
              When they place their first order: <span className="text-white">+{config.points_per_referral_first_order} pts</span>.
            </p>
            <div className="mt-5 flex items-center gap-2 px-4 py-3.5 rounded-xl border border-white/10 bg-white/[0.04]">
              <span className="text-gold-300 font-mono text-sm truncate flex-1" data-testid="referral-link-display">{fullReferralUrl}</span>
              <button
                onClick={copyLink}
                data-testid="referral-copy-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gold-500/[0.08] border border-gold-500/35 hover:bg-gold-500/15 text-gold-300 text-xs font-bold transition-colors"
              >
                <ClipboardCopy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>

            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-semibold mb-3">
                Share on
                <span className="ml-2 text-gold-300">+{config.points_per_social_share} pts per network / day</span>
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="share-buttons-grid">
                {SHARE_PROVIDERS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => openShare(SHARE_PROVIDERS.find((p) => p.key === key))}
                    data-testid={`share-btn-${key}`}
                    className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border border-zinc-800 hover:border-gold-500/45 hover:bg-gold-500/[0.04] transition-colors group"
                  >
                    <Icon className="w-5 h-5 text-gold-400 group-hover:scale-110 transition-transform" />
                    <span className="text-[10.5px] font-semibold text-white/65">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* REDEEM */}
          <div className="rounded-2xl border border-gold-500/25 bg-gradient-to-br from-zinc-950 to-black p-6 lg:p-7" data-testid="redeem-card">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-400 font-semibold mb-2">Use your gift credits</p>
            <h2 className="text-xl font-extrabold text-white tracking-tight">Redeem points for restaurant credit</h2>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">
              {ptsPerDollar} pts = $1.00 NoFee Gift Credit. Spend it at any participating restaurant — applied automatically at checkout.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {[ptsPerDollar, ptsPerDollar * 5, ptsPerDollar * 10].map((p) => {
                const disabled = ptsBalance < p;
                return (
                  <button
                    key={p}
                    onClick={() => redeem(p)}
                    disabled={disabled}
                    data-testid={`redeem-${p}-btn`}
                    className="rounded-xl border border-gold-500/30 hover:border-gold-500/70 disabled:opacity-40 disabled:cursor-not-allowed bg-gold-500/[0.04] hover:bg-gold-500/[0.08] py-3 transition-colors"
                  >
                    <div className="text-base font-black text-gold-400">${p / ptsPerDollar}</div>
                    <div className="text-[10.5px] text-white/55 mt-0.5">{p} pts</div>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[10.5px] text-white/40 leading-snug">
              Gift Credits are NoFee platform credits — <span className="text-white/55">not redeemable for cash</span>. Use them at participating restaurants.
            </p>

            {summary.gift_credit_cents > 0 && (
              <div className="mt-5 pt-5 border-t border-white/[0.06]">
                <button
                  type="button"
                  onClick={() => setRedeemOpen(true)}
                  data-testid="open-redeem-modal-btn"
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[14px] transition-all"
                >
                  Use my credits at a restaurant →
                </button>
                <p className="mt-2 text-[11px] text-white/40 text-center">
                  You have <span className="text-yellow-300 font-bold">${giftCreditBalance}</span> ready to spend.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <RedeemGiftModal
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        token={token}
        balanceCents={summary.gift_credit_cents || 0}
        onCreated={refresh}
      />

      {/* Founding Member block — early-believer recognition (no tiers, no Ambassador wording) */}
      {summary.is_founding_member && (
        <section className="pb-14" data-testid="founding-member-section">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div
              data-testid="founding-member-block"
              className="rounded-2xl border border-gold-500/40 bg-gradient-to-r from-gold-500/[0.06] via-amber-500/[0.04] to-gold-500/[0.06] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-300 to-gold-500 flex items-center justify-center flex-shrink-0 shadow-[0_12px_30px_-8px_rgba(250,204,21,0.5)]">
                <Sparkles className="w-6 h-6 text-black" strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-300 font-bold">
                    Founding Member
                  </p>
                  <span className="px-2 py-0.5 rounded-md bg-black/40 border border-gold-500/40 text-[10px] font-extrabold uppercase tracking-wider text-gold-300">
                    Limited — first 100 members
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                  A permanent badge for early believers in NoFee.
                </h3>
                <p className="mt-1.5 text-[13px] text-white/65 leading-relaxed max-w-2xl">
                  Founding Members are the first customers building the NoFee community alongside us. The badge is
                  <span className="text-white font-semibold"> permanent</span> — even as the platform grows, your founding status stays.
                  You also get <span className="text-white font-semibold">early access to new restaurants</span> in your area
                  and <span className="text-white font-semibold">first look at exclusive perks</span> as the community expands.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* History */}
      <section className="pb-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-4">Activity</h2>
          <div className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 to-black overflow-hidden">
            {history.length === 0 ? (
              <div className="px-6 py-12 text-center text-white/45 text-sm">
                No activity yet. Share your link to start earning!
              </div>
            ) : (
              <ul className="divide-y divide-zinc-800/80">
                {history.map((tx) => (
                  <li key={tx.id} className="flex items-center gap-4 px-5 py-3.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white capitalize">{(tx.kind || '').replace(/_/g, ' ')}</div>
                      <div className="text-[11px] text-white/45 mt-0.5">{new Date(tx.created_at).toLocaleString()}</div>
                    </div>
                    <StatusPill status={tx.status} />
                    <div className={`text-sm font-black ${tx.amount >= 0 ? 'text-gold-400' : 'text-rose-300'}`}>
                      {tx.amount >= 0 ? `+${tx.amount}` : tx.amount} pts
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Transparency disclaimer */}
          <p className="mt-6 text-xs text-white/40 max-w-3xl">
            <span className="text-gold-300 font-semibold">Rewards are verified before approval</span>{' '}
            to maintain fairness and prevent abuse. {config.disclaimer}
          </p>

          <div className="mt-8">
            <Link
              to="/customer"
              data-testid="back-to-customer-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-gold-400 hover:text-gold-300"
            >
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to NoFee
            </Link>
          </div>
        </div>
      </section>

      <FooterSection />

      {/* Premium floating referral CTA — only after first completed order */}
      <ReferralFloatingCTA
        eligible={!!summary.first_order_recorded}
        customerKey={summary.referral_code}
      />

      {/* Premium reward celebration toast (gold particles) */}
      <RewardCelebration />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, foot, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-zinc-800 hover:border-gold-500/30 bg-gradient-to-br from-zinc-950 to-black p-5 transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-gold-500/[0.06] border border-gold-500/30 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-gold-400" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <p className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">{label}</p>
        <div className="mt-1 text-2xl font-black text-white leading-tight">{value}</div>
        <p className="mt-1 text-[11px] text-white/40 leading-snug">{foot}</p>
      </div>
    </div>
  </div>
);

export default function CustomerRewards() {
  return (
    <LanguageProvider>
      <Inner />
    </LanguageProvider>
  );
}
